/**
 * Chain Provider Utility
 * Provides JsonRpcProviders for supported chains with caching and fallback logic.
 * Tracks rate limit (429) errors and switches to fallback RPCs when a threshold is exceeded.
 */

import { JsonRpcProvider } from 'ethers'
import { CHAIN_PRICE_CONFIGS, ChainPriceConfig } from './tokenPriceConfig'

// Provider cache
const providerCache: Map<number, JsonRpcProvider> = new Map()

// Connection status tracking
const connectionStatus: Map<number, { connected: boolean; lastCheck: number }> = new Map()
const CONNECTION_CHECK_INTERVAL = 30000 // 30 seconds

// Rate limit error tracking per chain
const rateLimitErrors: Map<number, number> = new Map()
const RATE_LIMIT_THRESHOLD = 10 // Switch RPC after this many rate limit errors

// Track which RPC URL index is active per chain (to skip rate-limited RPCs)
const currentRpcIndex: Map<number, number> = new Map()

/**
 * Report a rate limit (429) error for a chain's RPC provider.
 * When errors exceed the threshold, the cached provider is invalidated
 * so the next getChainProvider() call will try the next fallback RPC.
 */
export function reportRpcError(chainId: number): void {
  const count = (rateLimitErrors.get(chainId) ?? 0) + 1
  rateLimitErrors.set(chainId, count)

  if (count >= RATE_LIMIT_THRESHOLD) {
    const chainConfig = CHAIN_PRICE_CONFIGS[chainId]
    if (chainConfig) {
      const currentIdx = currentRpcIndex.get(chainId) ?? 0
      const nextIdx = (currentIdx + 1) % chainConfig.rpcUrls.length
      currentRpcIndex.set(chainId, nextIdx)
      console.warn(
        `[ChainProvider] Rate limit threshold exceeded for chain ${chainId} ` +
        `(${count} errors). Switching from RPC index ${currentIdx} to ${nextIdx}.`
      )
    }

    // Invalidate the cached provider so next call picks up the new RPC
    providerCache.delete(chainId)
    connectionStatus.delete(chainId)

    // Reset counter for the new RPC
    rateLimitErrors.set(chainId, 0)
  }
}

/**
 * Report a successful RPC call for a chain.
 * Resets the rate limit error counter so transient errors don't accumulate.
 */
export function reportRpcSuccess(chainId: number): void {
  if (rateLimitErrors.has(chainId)) {
    rateLimitErrors.set(chainId, 0)
  }
}

/**
 * Get a JsonRpcProvider for a chain
 * Uses caching and tries fallback RPCs if primary fails.
 * Starts from the tracked RPC index (advanced by reportRpcError on rate limiting).
 */
export async function getChainProvider(chainId: number): Promise<JsonRpcProvider> {
  // Check cache first
  const cached = providerCache.get(chainId)
  if (cached) {
    // Verify connection is still good (with throttling)
    const status = connectionStatus.get(chainId)
    if (status && status.connected && Date.now() - status.lastCheck < CONNECTION_CHECK_INTERVAL) {
      return cached
    }

    // Quick health check
    try {
      await cached.getBlockNumber()
      connectionStatus.set(chainId, { connected: true, lastCheck: Date.now() })
      return cached
    } catch {
      // Provider failed, remove from cache and try again
      providerCache.delete(chainId)
      connectionStatus.delete(chainId)
    }
  }

  const chainConfig = CHAIN_PRICE_CONFIGS[chainId]
  if (!chainConfig) {
    throw new Error(`No configuration found for chain ${chainId}`)
  }

  // Start from the tracked RPC index (defaults to 0)
  const startIndex = currentRpcIndex.get(chainId) ?? 0
  const rpcUrls = chainConfig.rpcUrls

  // Try each RPC URL starting from the tracked index
  for (let i = 0; i < rpcUrls.length; i++) {
    const index = (startIndex + i) % rpcUrls.length
    const rpcUrl = rpcUrls[index]
    try {
      const provider = new JsonRpcProvider(rpcUrl)
      // Test the connection
      await provider.getBlockNumber()

      // Cache successful provider and track index
      providerCache.set(chainId, provider)
      connectionStatus.set(chainId, { connected: true, lastCheck: Date.now() })
      currentRpcIndex.set(chainId, index)

      return provider
    } catch (err) {
      console.warn(`[ChainProvider] RPC ${rpcUrl} failed for chain ${chainId}, trying next...`)
    }
  }

  throw new Error(`No working RPC found for chain ${chainId} (${chainConfig.name})`)
}

/**
 * Get a provider synchronously if cached, otherwise return null
 * Useful for non-async contexts
 */
export function getCachedProvider(chainId: number): JsonRpcProvider | null {
  return providerCache.get(chainId) ?? null
}

/**
 * Get chain configuration
 */
export function getChainConfig(chainId: number): ChainPriceConfig | null {
  return CHAIN_PRICE_CONFIGS[chainId] ?? null
}

/**
 * Clear provider cache (useful for testing or forcing reconnection)
 */
export function clearProviderCache(): void {
  providerCache.clear()
  connectionStatus.clear()
  rateLimitErrors.clear()
  currentRpcIndex.clear()
}

/**
 * Get all supported chain IDs
 */
export function getSupportedChainIds(): number[] {
  return Object.keys(CHAIN_PRICE_CONFIGS).map(Number)
}
